import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Api from '../../services/api';

const TakeQuiz = () => {
    const { slug, encoded_id } = useParams();
    const navigate = useNavigate();

    const [quiz, setQuiz] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({}); // { questionId: optionNumber }
    const [validatedQuestions, setValidatedQuestions] = useState({}); // { questionId: true }
    const [timeLeft, setTimeLeft] = useState(0); // in seconds
    const [endTime, setEndTime] = useState(null); // absolute timestamp
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [score, setScore] = useState(0);

    const storageKey = `quiz_progress_${encoded_id}`;

    useEffect(() => {
        const fetchQuiz = async () => {
            try {
                const id = atob(encoded_id); // Decode the ID
                const response = await Api.GetRequest({}, 'quiz/' + id);
                if (response && response.status === 200 && response.quiz) {
                    setQuiz(response.quiz);

                    // Handle persistent state
                    const savedProgress = localStorage.getItem(storageKey);
                    let currentEndTime = Date.now() + (response.quiz.quiz_time || 600) * 1000;

                    if (savedProgress) {
                        try {
                            const parsed = JSON.parse(savedProgress);
                            if (parsed && typeof parsed === 'object') {
                                if (parsed.answers) setAnswers(parsed.answers);
                                if (parsed.validatedQuestions) setValidatedQuestions(parsed.validatedQuestions);
                                if (parsed.endTime) currentEndTime = parsed.endTime;
                            }
                        } catch (e) {
                            console.error("Error parsing saved quiz progress", e);
                        }
                    }

                    setEndTime(currentEndTime);
                    const remaining = Math.floor((currentEndTime - Date.now()) / 1000);
                    setTimeLeft(remaining > 0 ? remaining : 0);
                } else {
                    setError('Quiz not found');
                }
            } catch (err) {
                console.error(err);
                setError('Error loading quiz or invalid URL');
            } finally {
                setLoading(false);
            }
        };
        fetchQuiz();
    }, [encoded_id]);

    // Save progress whenever answers or validation changes
    useEffect(() => {
        if (!quiz || !endTime || isSubmitted) return;
        localStorage.setItem(storageKey, JSON.stringify({
            answers,
            validatedQuestions,
            endTime
        }));
    }, [answers, validatedQuestions, endTime, quiz, isSubmitted, storageKey]);

    useEffect(() => {
        let timer;
        if (!loading && !isSubmitted && timeLeft > 0 && endTime) {
            timer = setInterval(() => {
                const remaining = Math.floor((endTime - Date.now()) / 1000);
                if (remaining <= 0) {
                    setTimeLeft(0);
                } else {
                    setTimeLeft(remaining);
                }
            }, 1000);
        } else if (timeLeft <= 0 && !loading && !isSubmitted && quiz) {
            // Wait for next tick to avoid state updates during render issues if any
            setTimeout(() => handleSubmit(), 0);
        }
        return () => clearInterval(timer);
    }, [loading, isSubmitted, timeLeft, quiz, endTime]); // Re-runs but uses absolute endTime to prevent drift

    const handleOptionSelect = (questionId, optionNum) => {
        const perform = () => setAnswers({
            ...answers,
            [questionId]: optionNum,
        });

        if (isGoogleTranslateActive()) {
            // Defer updates briefly while Google Translate may be mutating DOM
            setTimeout(perform, 250);
        } else {
            perform();
        }
    };

    const handleNext = () => {
        const perform = () => {
            if (currentQuestionIndex < (quiz?.questions?.length || 0) - 1) {
                setCurrentQuestionIndex(currentQuestionIndex + 1);
            }
        };
        if (isGoogleTranslateActive()) setTimeout(perform, 200); else perform();
    };

    const handlePrev = () => {
        const perform = () => {
            if (currentQuestionIndex > 0) {
                setCurrentQuestionIndex(currentQuestionIndex - 1);
            }
        };
        if (isGoogleTranslateActive()) setTimeout(perform, 200); else perform();
    };

    const handleValidate = (questionId) => {
        const perform = () => setValidatedQuestions({
            ...validatedQuestions,
            [questionId]: true
        });
        if (isGoogleTranslateActive()) setTimeout(perform, 200); else perform();
    };

    const handleSubmit = () => {
        if (!quiz || !quiz.questions) return;

        let correctAnswers = 0;
        quiz.questions.forEach((q) => {
            if (answers[q.id] === q.correct_option) {
                correctAnswers += 1;
            }
        });

        const calculatedScore = Math.round((correctAnswers / quiz.questions.length) * 100);
        const performSubmit = () => {
            setScore(calculatedScore);
            setIsSubmitted(true);

            // Remove progress from local storage since the quiz is complete
            localStorage.removeItem(storageKey);

            // Navigate to the separate result page and pass the data via state
            navigate(`/quiz/${slug}/${encoded_id}/result`, {
                state: {
                    quiz: quiz,
                    score: calculatedScore,
                    totalQuestions: quiz.questions.length,
                    answers: answers
                }
            });
        };

        if (isGoogleTranslateActive()) setTimeout(performSubmit, 250); else performSubmit();
    };

    // Detect if Google Translate is active (heuristic)
    const isGoogleTranslateActive = () => {
        try {
            // cookie set by Google Translate when translation applied
            const match = document.cookie.match(/(?:^|; )googtrans=([^;]+)/);
            if (match && match[1] && match[1] !== '') return true;
            // banner iframe or combo presence
            if (document.querySelector('.goog-te-banner-frame') || document.querySelector('select.goog-te-combo')) return true;
            return false;
        } catch (e) {
            return false;
        }
    };

    const formatTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        if (h > 0) {
            return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        }
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    // Sanitize Google Translate wrappers: allow translation of text nodes
    // while preserving React-managed DOM structure. We wrap text nodes
    // with `.translatable-text` and strip any injected wrappers by
    // resetting `innerText` after translate mutates the DOM.
    React.useEffect(() => {
        const container = document.querySelector('section.px-2');
        if (!container || typeof MutationObserver === 'undefined') return;

        const sanitize = () => {
            document.querySelectorAll('.translatable-text').forEach((el) => {
                // Replace inner HTML with its text content to remove any
                // translation-added spans while keeping translated text.
                const txt = el.innerText || el.textContent || '';
                if (el.innerText !== txt) {
                    el.innerText = txt;
                } else {
                    // Force reassign to strip child nodes
                    el.innerText = txt;
                }
            });
        };

        const observer = new MutationObserver((mutations) => {
            let added = false;
            for (const m of mutations) {
                if (m.addedNodes && m.addedNodes.length) { added = true; break; }
            }
            if (added) {
                // Give Google Translate a moment to finish DOM updates
                setTimeout(sanitize, 50);
            }
        });

        observer.observe(container, { childList: true, subtree: true });
        // Also run once in case translation already applied
        setTimeout(sanitize, 100);

        return () => observer.disconnect();
    }, []);

    if (loading) {
        return (
            <div className="text-center py-5 mt-5">
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-2 text-muted">Loading Quiz Environment...</p>
            </div>
        );
    }

    if (error || !quiz) {
        return (
            <div className="text-center py-5 mt-5">
                <h3 className="text-danger">{error || 'Quiz not found'}</h3>
                <button className="btn btn_theme mt-3" onClick={() => navigate('/quizzes')}>Back to Quizzes</button>
            </div>
        );
    }

    if (isSubmitted) {
        return (
            <div className="text-center py-5 mt-5">
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-2 text-muted">Calculating Results...</p>
            </div>
        );
    }

    const currentQuestion = quiz?.questions ? quiz.questions[currentQuestionIndex] : null;

    if (!currentQuestion) {
        return (
            <div className="text-center py-5 mt-5">
                <h3 className="text-danger">No questions available for this quiz.</h3>
                <button className="btn btn_theme mt-3" onClick={() => navigate('/quizzes')}>Back to Quizzes</button>
            </div>
        );
    }

    const isCurrentValidated = (validatedQuestions || {})[currentQuestion.id] || false;
    const hasCurrentAnswer = (answers || {})[currentQuestion.id] !== undefined;

    return (
        <section className="px-2" style={{ marginTop: '50px', marginBottom: '50px', minHeight: '60vh' }}>
            <div className="container">
                <div className="row">

                    {/* Left Panel: Question Grid */}
                    <div className="col-lg-3 col-md-4 mb-4">
                        <div className="card shadow-sm border-0 p-3">
                            <h6 className="mb-3" style={{ fontWeight: 'bold' }}>All Questions</h6>
                            <div className="d-flex flex-wrap gap-1" style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '5px' }}>
                                        {(quiz.questions || []).map((q, idx) => {
                                    let bgColor = '#e9ecef'; // grey for unattempted
                                    let color = '#333';

                                    if ((validatedQuestions || {})[q.id]) {
                                        if ((answers || {})[q.id] === q.correct_option) {
                                            bgColor = '#198754'; // green
                                            color = '#fff';
                                        } else {
                                            bgColor = '#dc3545'; // red
                                            color = '#fff';
                                        }
                                    } else if ((answers || {})[q.id] !== undefined) {
                                        // Attempted but not validated
                                        bgColor = '#ffc107'; // yellow
                                        color = '#000';
                                    }

                                    const isCurrent = currentQuestionIndex === idx;

                                    return (
                                        <button
                                            key={q.id}
                                            onClick={() => setCurrentQuestionIndex(idx)}
                                            translate="no"
                                            style={{
                                                width: '35px', height: '35px',
                                                borderRadius: '6px',
                                                border: isCurrent ? '2px solid #000' : 'none',
                                                background: bgColor,
                                                color: color,
                                                fontWeight: 'bold',
                                                fontSize: '13px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: 0
                                            }}
                                        >
                                            <span className="translatable-text" translate="yes">{idx + 1}</span>
                                        </button>
                                    )
                                })}
                            </div>

                            <hr />
                            <div style={{ fontSize: '12px' }}>
                                <div className="d-flex align-items-center mb-1"><div style={{ width: '12px', height: '12px', background: '#e9ecef', borderRadius: '3px', marginRight: '8px' }}></div> Unattempted</div>
                                <div className="d-flex align-items-center mb-1"><div style={{ width: '12px', height: '12px', background: '#ffc107', borderRadius: '3px', marginRight: '8px' }}></div> Answered (Not Checked)</div>
                                <div className="d-flex align-items-center mb-1"><div style={{ width: '12px', height: '12px', background: '#198754', borderRadius: '3px', marginRight: '8px' }}></div> Correct</div>
                                <div className="d-flex align-items-center mb-1"><div style={{ width: '12px', height: '12px', background: '#dc3545', borderRadius: '3px', marginRight: '8px' }}></div> Incorrect</div>
                            </div>
                        </div>
                    </div>

                    {/* Right Panel: Quiz Area */}
                    <div className="col-lg-9 col-md-8">
                        {/* Quiz Header */}
                        <div className="d-flex justify-content-between align-items-center mb-4 p-3 shadow-sm rounded" style={{ background: '#f8f9fa', borderLeft: '4px solid var(--main-color, #198754)' }}>
                            <h4 className="mb-0" style={{ fontWeight: '600', color: '#333' }}>{quiz.title}</h4>
                            <div className="d-flex align-items-center" style={{ fontSize: '18px', fontWeight: 'bold', color: timeLeft <= 60 ? '#dc3545' : '#333' }}>
                                <i className="fa fa-clock-o me-2"></i> {formatTime(timeLeft)}
                            </div>
                        </div>

                        {/* Quiz Body */}
                        {currentQuestion ? (
                            <div className="card shadow-sm border-0 mb-4" key={currentQuestionIndex}>
                                <div className="card-body p-4">
                                    <div className="mb-4">
                                        <span className="badge bg-secondary mb-2" style={{ fontSize: '14px' }}>
                                            Question {currentQuestionIndex + 1} of {quiz.questions.length}
                                        </span>
                                        <h5 style={{ lineHeight: '1.5', color: '#000' }}><span className="translatable-text" translate="yes">{currentQuestion.question}</span></h5>
                                    </div>

                                    <div className="options-container">
                                        {[1, 2, 3, 4].map((optNum) => {
                                            const optionText = currentQuestion[`option_${optNum}`];
                                            if (!optionText) return null;

                                            const isSelected = answers[currentQuestion.id] === optNum;
                                            const isCorrectOption = currentQuestion.correct_option === optNum;

                                            let borderColor = isSelected ? 'var(--main-color, #198754)' : '#e9ecef';
                                            let bgColor = isSelected ? 'rgba(25, 135, 84, 0.05)' : '#fff';
                                            let textColor = '#333';
                                            let icon = null;

                                            if (isCurrentValidated) {
                                                if (isCorrectOption) {
                                                    borderColor = '#198754'; // green
                                                    bgColor = 'rgba(25, 135, 84, 0.1)';
                                                    icon = <i className="fa fa-check-circle" style={{ color: '#198754', fontSize: '20px', marginLeft: 'auto' }}></i>;
                                                } else if (isSelected && !isCorrectOption) {
                                                    borderColor = '#dc3545'; // red
                                                    bgColor = 'rgba(220, 53, 69, 0.1)';
                                                    icon = <i className="fa fa-times-circle" style={{ color: '#dc3545', fontSize: '20px', marginLeft: 'auto' }}></i>;
                                                } else {
                                                    borderColor = '#e9ecef';
                                                    bgColor = '#fff';
                                                }
                                            }

                                            return (
                                                <div
                                                    key={`${currentQuestionIndex}_${optNum}`}
                                                    onClick={() => !isCurrentValidated && handleOptionSelect(currentQuestion.id, optNum)}
                                                    style={{
                                                        padding: '15px 20px',
                                                        margin: '10px 0',
                                                        border: `2px solid ${borderColor}`,
                                                        borderRadius: '8px',
                                                        cursor: isCurrentValidated ? 'default' : 'pointer',
                                                        background: bgColor,
                                                        transition: 'all 0.2s',
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <div style={{
                                                        width: '24px', height: '24px', borderRadius: '50%',
                                                        border: `2px solid ${borderColor}`,
                                                        marginRight: '15px', display: 'flex', justifyContent: 'center', alignItems: 'center',
                                                                background: isSelected && !isCurrentValidated ? 'var(--main-color, #198754)' : (isCurrentValidated && isCorrectOption ? '#198754' : (isCurrentValidated && isSelected ? '#dc3545' : 'transparent'))
                                                    }}>
                                                        {isSelected && !isCurrentValidated && <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#fff' }}></div>}
                                                        {isCurrentValidated && (isCorrectOption || isSelected) && <i className="fa fa-check" style={{ color: '#fff', fontSize: '12px', display: isCorrectOption ? 'block' : 'none' }}></i>}
                                                        {isCurrentValidated && isSelected && !isCorrectOption && <i className="fa fa-times" style={{ color: '#fff', fontSize: '12px' }}></i>}
                                                    </div>
                                                    <span className="translatable-text" translate="yes" style={{ fontSize: '16px', color: textColor, fontWeight: isSelected || (isCurrentValidated && isCorrectOption) ? '500' : 'normal' }}>
                                                        {optionText}
                                                    </span>
                                                    {icon}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <h5 className="text-muted">No questions available for this quiz.</h5>
                            </div>
                        )}

                        {/* Navigation Footer */}
                        <div className="d-flex justify-content-between mt-4" key={`nav_${currentQuestionIndex}`}>
                                <button
                                    className="btn btn-outline-secondary px-4 py-2"
                                    onClick={handlePrev}
                                    disabled={currentQuestionIndex === 0}
                                    style={{ borderRadius: '25px', fontWeight: '500' }}
                                    translate="no"
                                >
                                    <i className="fa fa-angle-left me-2"></i> <span className="translatable-text" translate="yes">Previous</span>
                                </button>

                            <div>
                                {!isCurrentValidated && hasCurrentAnswer ? (
                                    <button
                                        className="btn btn-warning px-4 py-2"
                                        onClick={() => handleValidate(currentQuestion.id)}
                                        style={{ borderRadius: '25px', fontWeight: '600', color: '#000' }}
                                        translate="no"
                                    >
                                        <span className="translatable-text" translate="yes">Check Answer</span>
                                    </button>
                                ) : currentQuestionIndex === (quiz.questions?.length || 0) - 1 ? (
                                    <button
                                        className="btn btn_theme px-5 py-2 ms-2"
                                        onClick={handleSubmit}
                                        style={{ borderRadius: '25px', fontWeight: 'bold' }}
                                        translate="no"
                                    >
                                        <span className="translatable-text" translate="yes">Submit Quiz</span> <i className="fa fa-check ms-2"></i>
                                    </button>
                                ) : (
                                    <button
                                        className="btn btn_theme px-4 py-2 ms-2"
                                        onClick={handleNext}
                                        style={{ borderRadius: '25px', fontWeight: '500' }}
                                        translate="no"
                                    >
                                        <span className="translatable-text" translate="yes">Next</span> <i className="fa fa-angle-right ms-2"></i>
                                    </button>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </section>
    );
};

export default TakeQuiz;
